import type { FC, MouseEventHandler, ReactNode } from 'react'
import type { SortOption } from 'services/Search/utils/sort'

import { Drawer } from '@sok/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'

import {
  DESKTOP_LAYOUT_MEDIA_QUERY,
  DRAWER_BODY_STYLE,
  FULLSCREEN_DRAWER,
  SIDEBAR_DRAWER,
} from '../filters/FilterDrawer'
import { StyledDrawerTitle } from '../filters/styled'
import { SortRadio } from './sort-drawer/SortRadio'

interface SortDrawerProps {
  children?: ReactNode
  className?: string
  onClose: () => void
  open: boolean
  sortOptions: SortOption[]
}

const SortDrawerComponent: FC<SortDrawerProps> = ({ className, onClose, open, sortOptions }) => {
  const { t } = useTranslation()
  const isDesktopLayout = useMediaQuery(DESKTOP_LAYOUT_MEDIA_QUERY)
  const isClientSide = useIsClientSide()

  /**
   * Close Drawer when clicking on a sort option using a mouse,
   * but not when navigating via keyboard. This makes sure the
   * list is accessible-enough using the keyboard, while still
   * being convenient when using the mouse.
   */
  const handleClick = useCallback<MouseEventHandler<HTMLLIElement>>(
    (event) => {
      /** @see https://github.com/facebook/react/issues/7407 */
      if (event.clientX !== 0 && event.clientY !== 0) {
        onClose()
      }
    },
    [onClose],
  )

  return isClientSide ? (
    <Drawer
      bodyStyle={DRAWER_BODY_STYLE}
      contentWrapperStyle={isDesktopLayout ? SIDEBAR_DRAWER : FULLSCREEN_DRAWER}
      onClose={onClose}
      placement={isDesktopLayout ? 'right' : 'bottom'}
      title={<StyledDrawerTitle>{t`Sort`}</StyledDrawerTitle>}
      visible={open}
    >
      <ul className={className}>
        {sortOptions.map((sort) => (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
          <li key={`sort_${sort.join('_')}`} onClick={handleClick}>
            <SortRadio sort={sort} />
          </li>
        ))}
      </ul>
    </Drawer>
  ) : null
}

SortDrawerComponent.displayName = 'SortDrawer'

export const SortDrawer = styled(SortDrawerComponent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  listStyle: 'none',
  padding: theme.spacings.xSmall,

  /**
   * Override Design System `Radio` styles so that the radio
   * is on the right side, and padding works nicely.
   */
  'li label': {
    alignItems: 'center',
    display: 'flex',
    margin: 0,
    padding: `${theme.spacings.xxSmall} ${theme.spacings.xSmall}`,

    'span.checkmark': {
      marginLeft: 'auto',
      order: 2,
      position: 'relative',
    },
  },
}))
